import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Snowflake, XCircle } from "lucide-react";
import { HeaderNav } from "../components/HeaderNav";
import { authApi, errDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";

export const fmtEur = (v, lang) => new Intl.NumberFormat(lang === "en" ? "en-IE" : "fr-FR", { style: "currency", currency: "EUR" }).format(v);

const Shell = ({ children, testId }) => (
  <div className="frz-page"><HeaderNav /><main className="frz-section" data-testid={testId}>{children}</main></div>
);

export function Recharge() {
  const { t, te, lang } = useLang();
  const { user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState("");
  const [loadError, setLoadError] = useState(false);
  useEffect(() => { authApi.packs().then((d) => setPacks(d.packs)).catch(() => setLoadError(true)); }, []);
  const buy = async (id) => {
    setBusy(id);
    try {
      const { checkout_url: url } = await authApi.checkout(id);
      window.location.href = url;
    } catch (e) { toast.error(te(errDetail(e))); setBusy(""); }
  };
  return (
    <Shell testId="recharge-page">
      <div className="frz-section-head">
        <h1 className="frz-h1">{t("packs_title")}</h1>
        <p className="frz-muted">{t("packs_sub")} · {t("balance")} : <b data-testid="recharge-balance">{user.credits}</b></p>
      </div>
      {loadError && <p className="frz-error" data-testid="packs-load-error">{t("error_generic")} · <button type="button" className="frz-linkbtn" onClick={() => window.location.reload()}>↻</button></p>}
      <div className="frz-packs">
        {packs.map((p, i) => (
          <article key={p.id} className={`frz-pack ${i === 1 ? "is-featured" : ""}`} data-testid={`pack-card-${p.id}`}>
            {i === 1 && <span className="frz-pack-tag">{t("pack_popular")}</span>}
            <Snowflake size={28} className="frz-step-icon" />
            <h3 data-testid={`pack-credits-${p.id}`}>{p.credits} {t("credit_word_many")}</h3>
            <p className="frz-pack-price" data-testid={`pack-price-${p.id}`}>{fmtEur(p.amount, lang)}</p>
            <p className="frz-muted frz-small">{fmtEur(p.amount / p.credits, lang)} / {t("credit_word_one")}</p>
            <button type="button" className="frz-btn frz-btn-primary frz-btn-block" disabled={!!busy} onClick={() => buy(p.id)} data-testid={`pack-buy-${p.id}`}>
              {busy === p.id && <Loader2 size={16} className="spin" />} {t("buy")}
            </button>
          </article>
        ))}
      </div>
      <p className="frz-hint">{t("packs_note")}</p>
    </Shell>
  );
}

export function PaymentSuccess() {
  const { t } = useLang();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const [state, setState] = useState({ phase: "checking" });
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const sid = params.get("session_id");
    let n = 0;
    const poll = async () => {
      try {
        const s = await authApi.payStatus(sid);
        if (s.credited) { setState({ phase: "paid", credits: s.credits, balance: s.balance }); refresh(); return; }
        if (["cancelled", "expired", "amount_mismatch"].includes(s.status)) { setState({ phase: "failed" }); return; }
      } catch { /* retry */ }
      n += 1;
      if (n < 30) setTimeout(poll, 2000); else setState({ phase: "timeout" });
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Shell testId="payment-success-page">
      <div className="frz-auth-card frz-pay-card">
        {state.phase === "checking" && <p className="frz-muted" data-testid="payment-checking"><Loader2 size={18} className="spin" /> {t("pay_checking")}</p>}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 size={42} className="frz-ok-icon" />
            <h1 className="frz-h2" data-testid="payment-success-message">{t("pay_success", { n: state.credits })}</h1>
            <p className="frz-muted">{t("balance")} : <b data-testid="payment-new-balance">{state.balance}</b></p>
          </>
        )}
        {state.phase === "failed" && <h1 className="frz-h2" data-testid="payment-failed-message">{t("pay_cancelled")}</h1>}
        {state.phase === "timeout" && <p className="frz-error" data-testid="payment-timeout">{t("pay_timeout")}</p>}
        <Link to="/studio" className="frz-btn frz-btn-primary" data-testid="payment-back-studio">{t("back_studio")}</Link>
      </div>
    </Shell>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const tx = params.get("tx");
    if (tx) authApi.payCancel(tx).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Shell testId="payment-cancel-page">
      <div className="frz-auth-card frz-pay-card">
        <XCircle size={42} className="frz-err-icon" />
        <h1 className="frz-h2" data-testid="payment-cancel-message">{t("pay_cancelled")}</h1>
        <p className="frz-muted">{t("pay_cancelled_sub")}</p>
        <Link to="/recharge" className="frz-btn frz-btn-ghost" data-testid="payment-retry-button">{t("pay_retry")}</Link>
      </div>
    </Shell>
  );
}
